"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";

import { CardContent } from "./ui/card";
import { ArrowUpDown } from "lucide-react";
import info from "../assets/images/system.png";
import { Button } from "./ui/button";
import { CurrencySelector } from "../components/currencyDropdown2";
import { ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationProvider";
import { useSendFlowState } from "../hooks/useSendFlowState";
import {
  getTradeRate,
  getWalletAddresses,
} from "../services/digitalWalletService";
import CurrencyFormatter from "../utils/currencyFormatter";
import Authentication from "../assets/images/Authentication.png";
import ContactsPopup from "./ContactsPopup";
import DownloadAuthAppButton from "../components/DownloadAuthAppButton";
import { truncateDecimals } from "../utils/numberFormatter";

const Send = ({
  currentRate,
  onChangeAmount,
  onSelectAsset,
  onSelectChain,
  setSelectedAsset,
  selectedAsset,
  destinationAddress,
  setDestinationAddress,
  selectedNetwork,
  hotActionAsset,
  setTagNumber,
  onhandleQuote,
  parentAmount,
  setAmountFlag,
  amountFlag,
  setIsSending,
  isSending,
  zarAmount,
  twoFactor,
  tradingAssets,
  networks,
  onOpenAddContact,
  onAddressNotInContacts,
  currencyAssetsOverride,
}) => {
  const { updateState, isHydrated } = useSendFlowState();
  const [isContactsPopupOpen, setIsContactsPopupOpen] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const isFetchingAddresses = useRef(false);
  const addressesFetched = useRef(false);
  const [convertedValue, setConvertedValue] = useState(zarAmount);
  const [isBaseInput, setIsBaseInput] = useState(true);
  const [rate, setRate] = useState(currentRate);
  const [newAddress, setNewAddress] = useState(null);
  const [selectedChain, setSelectedChain] = useState(selectedNetwork);
  const [tag, setTag] = useState(null);
  const [flag, setFlag] = useState(false);
  const [amount, setAmount] = useState(parentAmount);

  useEffect(() => {
    if (
      isUserTypingRef.current ||
      isSwitchingRef.current ||
      isMaxClickedRef.current ||
      isAssetSwitchingRef.current
    ) {
      return;
    }
    // Only sync from parent if we don't have a local amount yet (initial load)
    if (
      parentAmount !== null &&
      parentAmount !== undefined &&
      (amount === null || amount === undefined || amount === "")
    ) {
      isUpdatingFromParentRef.current = true;
      setAmount(parentAmount);
      requestAnimationFrame(() => {
        isUpdatingFromParentRef.current = false;
      });
    }
  }, [parentAmount, amount]);

  useEffect(() => {
    if (selectedNetwork && selectedNetwork !== selectedChain) {
      setSelectedChain(selectedNetwork);
    }
  }, [selectedNetwork, selectedChain]);

  useEffect(() => {
    const currentRateValue =
      currentRate?.rate ||
      (typeof currentRate === "number" ? currentRate : null);
    if (
      currentRateValue &&
      typeof currentRateValue === "number" &&
      currentRateValue > 0
    ) {
      const currentLocalRateValue = rate?.rate || 0;
      if (currentRateValue !== currentLocalRateValue) {
        if (typeof currentRate === "object" && "rate" in currentRate) {
          setRate(currentRate);
        } else {
          setRate({ rate: currentRate });
        }
      }
    }
  }, [currentRate, rate]);
  const [maxFlag, setMaxFlag] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [minWithdrawal, setMinWithdrawal] = useState(0);

  const [errors, setErrors] = useState({
    destinationAddress: "",
    selectedAsset: "",
    amount: "",
    selectedNetwork: "",
  });

  const { settings } = useAuth();
  const { showPersistentMessage } = useNotification();
  const isUpdatingFromParentRef = useRef(false);
  const isUserTypingRef = useRef(false);
  const isSwitchingRef = useRef(false);
  const isMaxClickedRef = useRef(false);
  const isAssetSwitchingRef = useRef(false);
  const lastSentAmountRef = useRef({ crypto: null, zar: null });

  useEffect(() => {
    if (settings) {
      setIsEnabled(settings?.security?.twoFactorAuth?.enabled);
    }
  }, [settings]);

  const clearError = (fieldName) => {
    setErrors((prev) => ({
      ...prev,
      [fieldName]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {
      destinationAddress: "",
      selectedAsset: "",
      amount: "",
      selectedNetwork: "",
    };

    let isValid = true;

    if (!selectedAsset) {
      newErrors.selectedAsset = "Please select an asset.";
      isValid = false;
    }

    const rawAmount =
      typeof amount === "string" ? amount.replace(/,/g, "")?.trim() : amount;
    const parsedAmount = Number(rawAmount);
    if (
      rawAmount === "" ||
      rawAmount === null ||
      isNaN(parsedAmount) ||
      parsedAmount <= 0
    ) {
      newErrors.amount = "Please enter a valid amount greater than zero.";
      isValid = false;
    }

    const hasAddress =
      (typeof destinationAddress === "string" &&
        destinationAddress?.trim() !== "") ||
      (typeof destinationAddress === "object" &&
        destinationAddress !== null &&
        (destinationAddress.walletAddress || destinationAddress.addressName));

    if (!hasAddress) {
      newErrors.destinationAddress = "Please enter a valid wallet address.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const onSelectAssetRef = useRef(onSelectAsset);
  const showPersistentMessageRef = useRef(showPersistentMessage);

  useEffect(() => {
    onSelectAssetRef.current = onSelectAsset;
    showPersistentMessageRef.current = showPersistentMessage;
  });

  useEffect(() => {
    if (
      isUpdatingFromParentRef.current ||
      isSwitchingRef.current ||
      isMaxClickedRef.current ||
      isAssetSwitchingRef.current
    ) {
      return;
    }

    const currentRate = rate?.rate;
    if (!currentRate || isNaN(currentRate) || currentRate <= 0) {
      return;
    }

    const number = parseFloat(amount || 0);
    if (!number || isNaN(number)) {
      if (
        lastSentAmountRef.current.crypto !== 0 ||
        lastSentAmountRef.current.zar !== 0
      ) {
        setConvertedValue(0);
        if (onChangeAmount) {
          lastSentAmountRef.current = { crypto: 0, zar: 0 };
          onChangeAmount(0, 0);
        }
      }
      return;
    }

    const availableBalance = parseFloat(selectedAsset?.availableAmount || 0);

    if (!isBaseInput) {
      const zarValue = number * currentRate;
      if (isNaN(zarValue) || !isFinite(zarValue)) {
        setConvertedValue(0);
        return;
      }
      const formattedZar = parseFloat(zarValue.toFixed(2));
      if (isNaN(formattedZar)) {
        setConvertedValue(0);
        return;
      }
      setConvertedValue(formattedZar);

      if (number < minWithdrawal) {
        setFlag(true);
      } else {
        setFlag(false);
      }

      if (number > availableBalance) {
        setAmountFlag(true);
      } else {
        setAmountFlag(false);
      }

      if (
        onChangeAmount &&
        (lastSentAmountRef.current.crypto !== number ||
          lastSentAmountRef.current.zar !== formattedZar)
      ) {
        lastSentAmountRef.current = { crypto: number, zar: formattedZar };
        onChangeAmount(number, formattedZar);
      }
    } else {
      const cryptoValue = number / currentRate;
      if (isNaN(cryptoValue) || !isFinite(cryptoValue)) {
        setConvertedValue(0);
        return;
      }
      const asset = selectedAsset?.asset?.toLowerCase();
      const isTwoDecimalCurrency = asset === "usdc" || asset === "usdt";
      const formattedCrypto = isTwoDecimalCurrency
        ? parseFloat(cryptoValue.toFixed(2))
        : cryptoValue;
      if (isNaN(formattedCrypto)) {
        setConvertedValue(0);
        return;
      }
      setConvertedValue(formattedCrypto);

      if (formattedCrypto < minWithdrawal) {
        setFlag(true);
      } else {
        setFlag(false);
      }

      if (formattedCrypto > availableBalance) {
        setAmountFlag(true);
      } else {
        setAmountFlag(false);
      }

      if (
        onChangeAmount &&
        (lastSentAmountRef.current.crypto !== formattedCrypto ||
          lastSentAmountRef.current.zar !== number)
      ) {
        lastSentAmountRef.current = { crypto: formattedCrypto, zar: number };
        onChangeAmount(formattedCrypto, number);
      }
    }
  }, [
    amount,
    rate?.rate,
    isBaseInput,
    minWithdrawal,
    selectedAsset?.asset,
    selectedAsset?.availableAmount,
    onChangeAmount,
    setAmountFlag,
  ]);

  useEffect(() => {
    if (!selectedChain || !selectedChain.assetId || !selectedChain._id) return;

    const amount = parseFloat(selectedChain?.minimumWithdrawal?.$numberDecimal);
    setMinWithdrawal(amount);
  }, [selectedChain]);

  useEffect(() => {
    if (addressesFetched.current || isFetchingAddresses.current) return;

    const fetchAddresses = async () => {
      if (isFetchingAddresses.current) return;
      isFetchingAddresses.current = true;
      try {
        const data = await getWalletAddresses();
        setSavedAddresses(data.data.data.address || []);
        addressesFetched.current = true;
      } catch (err) {
      } finally {
        isFetchingAddresses.current = false;
      }
    };
    fetchAddresses();
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const saveTimer = setTimeout(() => {
      if (selectedAsset || amount || destinationAddress) {
        updateState({
          selectedAsset,
          amount,
          destinationAddress,
          selectedNetwork,
          tag,
        });
      }
    }, 500);

    return () => clearTimeout(saveTimer);
  }, [
    selectedAsset,
    amount,
    destinationAddress,
    selectedNetwork,
    tag,
    isHydrated,
    updateState,
  ]);

  const onTagChange = (e) => {
    const newTag = e.target.value;
    if (/^\d*\.?\d*$/.test(newTag)) {
      setTagNumber(newTag);
      setTag(newTag);
    }
  };
  const rawValue = 0;
  const value = Number(rawValue);
  const fee = value.toFixed(8);

  const trueMax = parseFloat(selectedAsset?.availableAmount) - parseFloat(fee);

  const onTextChange = (e) => {
    setAmountFlag(false);
    isUserTypingRef.current = true;

    const rawValue = e.target.value;
    let newAmount = typeof rawValue === "number" ? String(rawValue) : rawValue;

    if (!/^\d*\.?\d*$/.test(newAmount)) {
      isUserTypingRef.current = false;
      return;
    }

    if (newAmount === "" || newAmount === ".") {
      setAmount("");
      setConvertedValue(0);
      setTimeout(() => {
        isUserTypingRef.current = false;
      }, 100);
      return;
    }

    // Remove leading zeros (but keep "0." for decimals)
    if (newAmount.length > 1 && newAmount[0] === "0" && newAmount[1] !== ".") {
      newAmount = newAmount.replace(/^0+/, "") || "0";
    }

    const numericAmount = Number(newAmount);
    if (isNaN(numericAmount) || !isFinite(numericAmount)) {
      isUserTypingRef.current = false;
      return;
    }

    setAmount(newAmount);

    if (newAmount.trim() !== "") {
      clearError("amount");
    }

    const numericTrueMax = Number(trueMax);
    if (!isNaN(numericTrueMax) && numericAmount !== numericTrueMax) {
      setMaxFlag(false);
    }

    const numericMin = Number(minWithdrawal);
    if (!isNaN(numericMin) && numericMin > 0) {
      const rateValue = rate?.rate;
      if (isBaseInput && rateValue && rateValue > 0) {
        const cryptoEquivalent = numericAmount / rateValue;
        setFlag(cryptoEquivalent < numericMin);
      } else if (!isBaseInput) {
        setFlag(numericAmount < numericMin);
      } else {
        setFlag(false);
      }
    } else {
      setFlag(false);
    }

    setTimeout(() => {
      isUserTypingRef.current = false;
    }, 100);
  };

  const maxQuoteAmount = () => {
    isMaxClickedRef.current = true;

    const availableAmount = selectedAsset?.availableAmount;
    const rateValue = rate?.rate;

    if (!availableAmount || availableAmount <= 0) {
      isMaxClickedRef.current = false;
      return;
    }

    if (!rateValue || rateValue <= 0) {
      isMaxClickedRef.current = false;
      return;
    }

    const feeValue = parseFloat(fee || 0);
    const maxCrypto = parseFloat(availableAmount) - feeValue;

    if (isNaN(maxCrypto) || maxCrypto <= 0) {
      isMaxClickedRef.current = false;
      return;
    }

    const asset = selectedAsset?.asset?.toLowerCase();
    const isTwoDecimalCurrency = asset === "usdc" || asset === "usdt";
    const zarValue = parseFloat((maxCrypto * rateValue).toFixed(2));

    let formattedAmount;
    let cryptoAmount;
    let zarAmount;

    if (!isBaseInput) {
      formattedAmount = isTwoDecimalCurrency
        ? String(Math.round(maxCrypto * 100) / 100)
        : String(Math.round(maxCrypto * 100000000) / 100000000);
      cryptoAmount = maxCrypto;
      zarAmount = zarValue;
      setConvertedValue(zarValue);
    } else {
      formattedAmount = String(Math.round(zarValue * 100) / 100);
      cryptoAmount = maxCrypto;
      zarAmount = zarValue;
      setConvertedValue(
        isTwoDecimalCurrency ? parseFloat(maxCrypto.toFixed(2)) : maxCrypto
      );
    }

    setAmount(formattedAmount);
    setMaxFlag(false);
    clearError("amount");

    if (onChangeAmount) {
      lastSentAmountRef.current = { crypto: cryptoAmount, zar: zarAmount };
      onChangeAmount(cryptoAmount, zarAmount);
    }

    const numericMin = Number(minWithdrawal);
    if (!isNaN(numericMin) && numericMin > 0) {
      setFlag(maxCrypto < numericMin);
    } else {
      setFlag(false);
    }

    const availableBalance = parseFloat(selectedAsset?.availableAmount || 0);
    setAmountFlag(maxCrypto > availableBalance);

    setTimeout(() => {
      isMaxClickedRef.current = false;
    }, 200);
  };

  const switchConversion = () => {
    isSwitchingRef.current = true;

    const currentAmount = amount;
    const currentConverted = convertedValue;
    const switchingToZarMode = !isBaseInput;
    const asset = selectedAsset?.asset?.toLowerCase();
    const isTwoDecimalCurrency = asset === "usdc" || asset === "usdt";

    let newAmount;
    if (typeof currentConverted === "number" && currentConverted !== 0) {
      if (switchingToZarMode) {
        newAmount = String(Math.round(currentConverted * 100) / 100);
      } else {
        newAmount = isTwoDecimalCurrency
          ? String(Math.round(currentConverted * 100) / 100)
          : String(Math.round(currentConverted * 100000000) / 100000000);
      }
    } else {
      newAmount = "0";
    }

    setIsBaseInput((prev) => !prev);
    setAmount(newAmount);
    setConvertedValue(parseFloat(currentAmount) || 0);

    requestAnimationFrame(() => {
      isSwitchingRef.current = false;
    });
  };

  const handleBalanceAmount = useCallback(
    async (balance) => {
      if (!balance || !balance?.assetId || !balance?.asset) {
        return;
      }

      if (balance?.assetId !== selectedAsset?.assetId) {
        isAssetSwitchingRef.current = true;

        try {
          const data = await getTradeRate(
            balance.assetId,
            "sell",
            `${balance.asset}-zar`
          );
          const newRate = data?.data?.data;

          if (
            newRate?.rate &&
            typeof newRate.rate === "number" &&
            newRate.rate > 0
          ) {
            setRate(newRate);
          }

          setSelectedAsset(balance);
          clearError("selectedAsset");

          if (newRate?.rate && onSelectAssetRef.current) {
            onSelectAssetRef.current(balance, newRate.rate);
          }
        } catch (err) {
          console.error("[Send] Error fetching rate:", err);
          setSelectedAsset(balance);
          clearError("selectedAsset");
        }

        setTimeout(() => {
          isAssetSwitchingRef.current = false;
        }, 100);
      }
    },
    [selectedAsset, setSelectedAsset]
  );

  useEffect(() => {
    if (!hotActionAsset) return;
    handleBalanceAmount(hotActionAsset);
  }, [hotActionAsset, handleBalanceAmount]);

  const handleChain = (net) => {
    const newChain = net;
    if (onSelectChain) {
      onSelectChain(newChain);
    }
    setSelectedChain(newChain);
    clearError("selectedNetwork");
  };

  const handleContactSelect = async (wallet) => {
    setDestinationAddress(wallet);

    const assetMatch = tradingAssets?.find((a) => a.assetId === wallet.asset);

    const chainMatch = networks?.find(
      (net) => net.assetId === wallet.asset && net._id === wallet.chain
    );

    if (assetMatch) {
      if (assetMatch?.assetId !== selectedAsset?.assetId) {
        setSelectedAsset(assetMatch);
        try {
          const data = await getTradeRate(
            assetMatch.assetId,
            "sell",
            `${assetMatch.asset}-zar`
          );
          const newRate = data?.data?.data;
          if (
            newRate?.rate &&
            typeof newRate.rate === "number" &&
            newRate.rate > 0
          ) {
            setRate(newRate);
            if (onSelectAsset) {
              onSelectAsset(assetMatch, newRate.rate);
            }
          }
        } catch (err) {
          console.error("[Send] Error fetching rate for contact asset:", err);
        }
      }
      clearError("selectedAsset");
    } else {
      setErrors((prev) => ({
        ...prev,
        selectedAsset: "Selected asset not available in your wallet",
      }));
    }

    if (chainMatch) {
      if (chainMatch?._id !== selectedChain?._id) {
        setSelectedChain(chainMatch);
        if (onSelectChain) {
          onSelectChain(chainMatch);
        }
      }
      clearError("selectedNetwork");
    } else {
      setErrors((prev) => ({
        ...prev,
        selectedNetwork: "Selected network not available",
      }));
    }

    if (wallet.tag) {
      setTag(wallet.tag);
      setTagNumber(wallet.tag);
    }

    setIsContactsPopupOpen(false);
    clearError("destinationAddress");
  };

  const handleSend = async () => {
    if (!validateForm()) return;

    const addressStr =
      typeof destinationAddress === "object" && destinationAddress !== null
        ? (
            destinationAddress.walletAddress ||
            destinationAddress.addressName ||
            ""
          ).trim()
        : String(destinationAddress || "").trim();

    const isAddressFromContact =
      typeof destinationAddress === "object" &&
      destinationAddress !== null &&
      (destinationAddress.walletAddress != null ||
        destinationAddress.addressName != null);

    if (!isAddressFromContact && addressStr !== "" && onAddressNotInContacts) {
      let addresses = savedAddresses;
      if (addresses.length === 0) {
        try {
          const data = await getWalletAddresses();
          addresses = data?.data?.data?.address || [];
          setSavedAddresses(addresses);
          addressesFetched.current = true;
        } catch (err) {
          addresses = [];
        }
      }
      const isInContacts = addresses.some(
        (addr) =>
          (addr.walletAddress || "").trim().toLowerCase() ===
          addressStr.toLowerCase()
      );
      if (!isInContacts) {
        onAddressNotInContacts({
          address: addressStr,
          selectedAsset,
          selectedNetwork,
          amount,
          tag: tag || null,
        });
        return;
      }
    }

    const numericAmount = parseFloat(amount || 0);
    let cryptoAmount = null;

    if (isBaseInput) {
      cryptoAmount = parseFloat(convertedValue || 0);
    } else {
      cryptoAmount = numericAmount;
    }

    if (!cryptoAmount || cryptoAmount <= 0 || isNaN(cryptoAmount)) {
      if (numericAmount > 0 && rate?.rate) {
        cryptoAmount = isBaseInput ? numericAmount / rate.rate : numericAmount;
      }
    }

    updateState({
      selectedAsset,
      amount,
      destinationAddress,
      selectedNetwork,
      tag: tag ? tag : "",
      currentStep: "quote",
    });

    onhandleQuote(cryptoAmount > 0 ? cryptoAmount : null);
    setIsSending(true);
  };

  const formatter = new CurrencyFormatter();
  return (
    <CardContent className="px-2 sm:px-6">
      <div className="mb-6 w-full">
        <label className="block text-[14px] text-[#6D6584] mb-2">Send to</label>

        <div
          className={`border-[1px] h-[70px] md:h-[88px] rounded-lg px-6 py-2 bg-transparent flex items-center transition-colors ${
            errors.destinationAddress
              ? "border-rose-500 border-2"
              : "border-[#CECBD7]"
          }`}
        >
          <input
            type="text"
            placeholder="Enter wallet address..."
            className="flex-1 outline-none bg-transparent lg:text-[16px] text-[14px] font-semibold text-[#6D6584] w-[90%]"
            value={
              destinationAddress?.walletAddress ||
              destinationAddress ||
              newAddress ||
              ""
            }
            onChange={(e) => {
              const value = e.target.value;
              setDestinationAddress(value);
              setNewAddress(value);
              if (value && String(value).trim() !== "") {
                clearError("destinationAddress");
              }
            }}
          />

          <Button
            onClick={async () => {
              if (!isFetchingAddresses.current && !addressesFetched.current) {
                isFetchingAddresses.current = true;
                try {
                  const data = await getWalletAddresses();
                  setSavedAddresses(data.data.data.address || []);
                  addressesFetched.current = true;
                } catch (err) {
                } finally {
                  isFetchingAddresses.current = false;
                }
              }
              setIsContactsPopupOpen(true);
            }}
            variant="link"
            className="lg:text-[14px] text-[12px] text-[#33125F] !border-[1px] !border-[#EBE9EF] !transform-none rounded-full font-semibold lg:w-[173px] lg:h-[48px] w-auto h-[38px] relative -right-[15px]"
          >
            Open Contacts
            <ChevronDown className="text-gray-400 hidden lg:block" />
          </Button>
        </div>
        {errors.destinationAddress && (
          <p className="mt-2 text-sm text-rose-500">
            {errors.destinationAddress}
          </p>
        )}
      </div>

      <div>
        <CurrencySelector
          onSelectNet={handleChain}
          onSelect={handleBalanceAmount}
          setSelectedAsset={setSelectedAsset}
          selectedAsset={selectedAsset}
          isSend={true}
          hotActionAsset={hotActionAsset}
          isEightyeightPay={false}
          rate={rate?.rate}
          assetsOverride={currencyAssetsOverride}
        />
        {errors.selectedAsset && (
          <p className="mt-2 text-sm text-rose-500">{errors.selectedAsset}</p>
        )}
        {errors.selectedNetwork && (
          <p className="mt-2 text-sm text-rose-500">{errors.selectedNetwork}</p>
        )}
      </div>

      <div className="mt-6 mb-6 w-full">
        <label className="block text-[14px] text-[#6D6584] mb-2">
          Enter amount
        </label>

        <div className="space-y-2">
          <div
            className={`relative flex items-center gap-4 rounded-2xl py-3 px-6 h-[70px] md:h-[88px] border transition-colors ${
              flag || errors.amount
                ? "border-2 border-rose-500"
                : "border-[#CECBD7]"
            }`}
          >
            {isBaseInput ? (
              <p className="text-[24px] font-semibold text-[#6D6584]">R</p>
            ) : (
              <p className="text-[24px] font-semibold text-[#6D6584]">
                {selectedAsset?.asset?.toUpperCase() || ""}
              </p>
            )}
            <input
              type="text"
              value={amount ?? ""}
              onChange={(e) => {
                onTextChange(e);
              }}
              className={`flex-1 w-[60%] text-lg font-semibold bg-transparent outline-none 
              focus-visible:ring-0 focus-visible:outline-none transition-colors duration-200 ease-in-out
              ${flag || errors.amount ? "text-rose-500" : "text-[#6D6584]"}`}
              placeholder="0"
            />

            <div className="text-gray-500 hidden lg:block">
              ≈{" "}
              {convertedValue === " " ||
              isNaN(convertedValue) ||
              !isFinite(convertedValue) ||
              isNaN(parseFloat(String(convertedValue)))
                ? (() => {
                    if (isBaseInput) {
                      const asset = selectedAsset?.asset?.toLowerCase();
                      const isTwoDecimalCurrency =
                        asset === "usdc" || asset === "usdt";
                      return isTwoDecimalCurrency ? "0.00" : "0.0000000";
                    }
                    return "R 0.00";
                  })()
                : (() => {
                    if (isBaseInput) {
                      const asset = selectedAsset?.asset?.toLowerCase();
                      const isTwoDecimalCurrency =
                        asset === "usdc" || asset === "usdt";
                      const decimals = isTwoDecimalCurrency ? 2 : 7;
                      const parsed = parseFloat(String(convertedValue));
                      if (isNaN(parsed) || !isFinite(parsed)) {
                        return isTwoDecimalCurrency ? "0.00" : "0.0000000";
                      }
                      return truncateDecimals(parsed, decimals);
                    } else {
                      const parsed = parseFloat(String(convertedValue));
                      if (isNaN(parsed) || !isFinite(parsed)) {
                        return "R 0.00";
                      }
                      return `R ${truncateDecimals(parsed, 2)}`;
                    }
                  })()}
              {isBaseInput && selectedAsset?.asset?.toUpperCase()}{" "}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={switchConversion}
                variant="outline"
                size="icon"
                className="rounded-full"
              >
                <ArrowUpDown className="h-4 w-4" />
              </Button>
              <Button
                onClick={maxQuoteAmount}
                variant="outline"
                className="rounded-full"
              >
                Max
              </Button>
            </div>
          </div>

          <div className="border rounded-lg px-4 py-2 lg:hidden block text-center mt-2 text-gray-500">
            ≈{" "}
            {convertedValue === " " ||
            isNaN(convertedValue) ||
            !isFinite(convertedValue) ||
            isNaN(parseFloat(String(convertedValue)))
              ? (() => {
                  if (isBaseInput) {
                    const asset = selectedAsset?.asset?.toLowerCase();
                    const isTwoDecimalCurrency =
                      asset === "usdc" || asset === "usdt";
                    return isTwoDecimalCurrency ? "0.00" : "0.0000000";
                  }
                  return "R 0.00";
                })()
              : (() => {
                  if (isBaseInput) {
                    const asset = selectedAsset?.asset?.toLowerCase();
                    const isTwoDecimalCurrency =
                      asset === "usdc" || asset === "usdt";
                    const decimals = isTwoDecimalCurrency ? 2 : 7;
                    const parsed = parseFloat(String(convertedValue));
                    if (isNaN(parsed) || !isFinite(parsed)) {
                      return isTwoDecimalCurrency ? "0.00" : "0.0000000";
                    }
                    return truncateDecimals(parsed, decimals);
                  } else {
                    const parsed = parseFloat(String(convertedValue));
                    if (isNaN(parsed) || !isFinite(parsed)) {
                      return "R 0.00";
                    }
                    return `R ${truncateDecimals(parsed, 2)}`;
                  }
                })()}
            {isBaseInput && selectedAsset?.asset?.toUpperCase()}{" "}
          </div>

          {errors.amount && (
            <p className="mt-2 text-sm text-rose-500">{errors.amount}</p>
          )}
          {!errors.amount && flag ? (
            <p className="mt-1 text-sm text-rose-500">
              This amount is lower than minimum amount i.e.{" "}
              {formatter.format(
                selectedChain?.minimumWithdrawal?.$numberDecimal,
                selectedAsset?.asset?.toUpperCase() || ""
              )}
            </p>
          ) : (
            !errors.amount &&
            amountFlag && (
              <p className="mt-1 text-sm text-rose-500">
                Total amount with fee exceeds your asset balance
              </p>
            )
          )}
          {!errors.amount && maxFlag && (
            <p className="mt-1 text-end text-sm text-rose-500">
              Subtracted fee from maximum amount{" "}
              {formatter.format(0, selectedAsset?.asset?.toUpperCase() || "")}
            </p>
          )}
          {selectedAsset?.asset === "xrp" && (
            <>
              <label className="!mt-6 block text-[14px] text-[#6D6584] mb-2">
                Tag Number
              </label>
              <div
                className={`flex items-center w-full gap-4 rounded-2xl p-6 border transition-colors h-[70px] md:h-[88px] ${
                  errors.selectedAsset ===
                  "Please select an asset before adding a contact."
                    ? "border-rose-500 border-2"
                    : "border-[#CECBD7]"
                }`}
              >
                <input
                  type="text"
                  value={tag ?? ""}
                  onChange={onTagChange}
                  className="flex-1 text-base md:text-sm font-semibold bg-transparent outline-none focus-visible:ring-0 focus-visible:outline-none transition-colors duration-200 ease-in-out text-brand-primary"
                  placeholder="Enter tag number"
                />
              </div>
            </>
          )}
        </div>
      </div>
      {!isEnabled && (
        <div className="border-[1px] border-[#EBE9EF] p-4 sm:p-8 mb-6 rounded-2xl">
          <div className="flex flex-col sm:flex-row sm:items-center mb-4">
            <img
              src={Authentication}
              alt="Authentication"
              width={80}
              height={60}
              className="mb-3 sm:mb-0 sm:mr-4"
            />
            <div>
              <p className="text-[#423E51] font-semibold text-[16px] mb-2">
                2-Factor Authentication Not Active
              </p>
              <p className="text-[#6D6584] text-[14px]">
                80eight requires 2-Factor Authentication in order to keep your
                account secure. By using an authenticator app, you ensure no one
                else can log in and transact using your account.{" "}
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:justify-between">
            <Button
              className="bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white px-4 sm:px-6 py-3 sm:py-4 rounded-full font-semibold hover:opacity-90 transition-opacity w-full sm:w-[49%] h-[48px]"
              onClick={twoFactor}
            >
              Enable 2-Factor Authentication
            </Button>
            <DownloadAuthAppButton className="bg-transparent px-4 sm:px-6 py-3 sm:py-4 transition-opacity w-full sm:w-[49%] h-[48px]" />
          </div>
        </div>
      )}

      {selectedChain?.withdrawalWarning && (
        <div className="flex mb-6 items-center justify-center mt-6 center gap-4 bg-orange-50 font-semibold rounded-2xl p-4 text-orange-800 text-[12px] sm:text-[14px]">
          <img src={info} alt="info" width={16} height={16} />
          {selectedChain?.withdrawalWarning}
        </div>
      )}
      <div className="w-full flex justify-center">
        <Button
          onClick={handleSend}
          disabled={!isEnabled}
          isLoading={isSending}
          className={`px-6 py-4 rounded-full font-semibold transition-opacity w-full sm:min-w-[302px] max-w-sm h-[48px] text-sm sm:text-base ${
            isSending || !isEnabled
              ? "bg-[#F2F1F4] text-[#9D96AF] cursor-not-allowed"
              : "bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white hover:opacity-90"
          }`}
        >
          {isSending ? "Processing..." : "Continue"}
        </Button>
      </div>

      <ContactsPopup
        isOpen={isContactsPopupOpen}
        onClose={() => setIsContactsPopupOpen(false)}
        onSelectContact={handleContactSelect}
        onAddNewContact={() => {
          onOpenAddContact?.();
        }}
        savedAddresses={savedAddresses}
      />
    </CardContent>
  );
};

export default Send;
